import React, { useState, useEffect, useContext, useMemo, useRef } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import api from '../api/axiosConfig';
import AuthContext from '../contexts/AuthContext';
import { useModal } from '../contexts/ModalContext';
import InspectionDraft, { fingerprintOf } from '../services/InspectionDraft';
import imageCompression from 'browser-image-compression';

const MAX_PHOTOS_PER_ITEM = 4;
// 서버도 1200px JPEG 로 한 번 더 줄인다. 이쪽은 저장 용량이 아니라 작업자의
// 데이터 요금과 업로드 시간을 아끼는 것이 목적이다.
const COMPRESSION_OPTIONS = {
  maxSizeMB: 0.5,
  maxWidthOrHeight: 1200,
  useWebWorker: true,
};

const InspectionPage = () => {
  const { id } = useParams(); // facilityId
  const [searchParams] = useSearchParams();
  // 계획의 정거장에서 시작했으면 그 정거장. 점검표도 제출 대상도 여기서 나온다.
  const stopIdParam = searchParams.get('stopId');
  const navigate = useNavigate();
  const { user } = useContext(AuthContext);
  const { showAlert, showConfirm } = useModal();

  // Data State
  const [facility, setFacility] = useState(null);
  const [section, setSection] = useState(null);
  const [loading, setLoading] = useState(true);

  // Form State
  const [checks, setChecks] = useState({}); // { itemId: boolean }
  const [subChecks, setSubChecks] = useState({}); // { itemId: { optionIndex: boolean } } for checkbox/choice
  const [answers, setAnswers] = useState({}); // { itemId: '네' | '아니오' } for check_yn
  const [photos, setPhotos] = useState({}); // { itemId: [{ url, file }] }
  const [memos, setMemos] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  // 이 점검 한 번을 가리키는 값. 서버가 항목당 사진 장수를 이 단위로 센다.
  // 어제 하다 만 점검의 사진이 오늘 한도를 깎지 않도록.
  const sessionIdRef = useRef(
    (crypto.randomUUID && crypto.randomUUID()) ||
    `${Date.now()}-${Math.random().toString(36).slice(2)}`
  );
  // 이어할지 물어보고 답을 받기 전까지는 저장하지 않는다. 확인 창이 떠 있는
  // 사이 빈 상태가 초안을 덮어써 버리면 복원할 것이 없어진다.
  const [draftReady, setDraftReady] = useState(false);
  const draftAskedRef = useRef(false);

  // 임시저장을 무엇으로 구분할지. 건물 전체를 도는 정거장은 구역 id 가 없어서
  // 구역만으로는 키가 서지 않는다.
  const draftKey = stopIdParam ? `stop-${stopIdParam}` : section?.id;

  // Section headers are dividers, not questions: they are never answered,
  // never required, and don't count towards progress.
  const isAnswerable = (item) => item.type !== 'SECTION';

  // Normalize items (map backend types to frontend types) - Moved to top for scope access
  const templateItems = useMemo(() => {
    const rawTemplateItems = section?.checklistTemplate?.items || [];
    return rawTemplateItems.map(item => {
      const typeMap = {
        '체크리스트': 'check',
        '체크박스': 'checkbox',
        '체크리스트(네/아니오)': 'check_yn',
        '텍스트 입력': 'input',
        '객관식 선택': 'choice',
        '섹션': 'SECTION'
      };
      const photoMap = {
        '없음': 'none',
        '선택': 'optional',
        '필수': 'required'
      };

      return {
        ...item,
        type: typeMap[item.responseType] || item.responseType || 'check',
        photoAttachmentOption: photoMap[item.photoAttachmentOption] || item.photoAttachmentOption || 'none'
      };
    });
  }, [section]);

  // 저장된 주소는 비공개라 그대로는 안 열린다. 지금 열리는 주소로 바꿔 받는다.
  const restorePhotos = async (saved) => {
    const urls = Object.values(saved).flat().map((p) => p.url).filter(Boolean);
    if (!urls.length) {
      setPhotos(saved);
      return;
    }
    try {
      const res = await api.post('/reports/uploads/sign', { urls });
      const signed = new Map(urls.map((url, i) => [url, res.data.urls[i]]));
      const next = {};
      for (const [itemId, list] of Object.entries(saved)) {
        next[itemId] = list.map((p) => ({ ...p, viewUrl: signed.get(p.url) || p.url }));
      }
      setPhotos(next);
    } catch (err) {
      // 서명을 못 받아도 점검은 이어갈 수 있어야 한다. 미리보기만 비어 보인다.
      setPhotos(saved);
    }
  };

  // 하다 만 점검이 있으면 이어할지 묻는다.
  useEffect(() => {
    if (loading || !section || draftAskedRef.current) return;
    draftAskedRef.current = true;

    const draft = InspectionDraft.read(id, draftKey);
    if (!draft) {
      setDraftReady(true);
      return;
    }

    // 항목이 바뀐 초안은 되살리지 않는다. 답변이 항목 id 로 물려 있어서,
    // 사라진 항목의 답변을 되살리면 화면과 어긋난다.
    if (draft.fingerprint !== fingerprintOf(templateItems)) {
      InspectionDraft.clear(id, draftKey);
      setDraftReady(true);
      showAlert('알림', '점검표가 변경되어 이전에 저장된 내용을 불러올 수 없습니다.', 'warning');
      return;
    }

    const savedAt = new Date(draft.savedAt);
    const when = `${savedAt.getMonth() + 1}월 ${savedAt.getDate()}일 ` +
      `${String(savedAt.getHours()).padStart(2, '0')}:${String(savedAt.getMinutes()).padStart(2, '0')}`;

    showConfirm('이어서 하기', `${when}에 저장된 점검이 있습니다. 이어서 하시겠습니까?`)
      .then((resume) => {
        if (resume) {
          setChecks(draft.checks || {});
          setSubChecks(draft.subChecks || {});
          setAnswers(draft.answers || {});
          setMemos(draft.memos || '');
          // 초안은 12시간 살아 있는데 사진 주소의 서명은 한 시간이다. 초안에
          // 서명된 주소를 담아 둘 수 없으므로 열 때 다시 받는다.
          restorePhotos(draft.photos || {});
          // 사진 장수를 세는 단위라 같이 되살려야 한다. 새 값으로 시작하면
          // 이미 올린 사진이 한도에서 빠져 항목당 4장을 넘길 수 있다.
          if (draft.sessionId) sessionIdRef.current = draft.sessionId;
        } else {
          InspectionDraft.clear(id, draftKey);
        }
        setDraftReady(true);
      });
  }, [loading, section, draftKey, templateItems, id, showAlert, showConfirm]);

  // 바뀔 때마다 담아 둔다. 사진은 이미 서버에 올라가 URL 만 남아 있어 그대로
  // 직렬화된다. 올라가는 중인 것은 URL 이 없으므로 뺀다.
  useEffect(() => {
    if (!draftReady || !section) return;

    const savedPhotos = {};
    for (const [itemId, list] of Object.entries(photos)) {
      const uploaded = list.filter((photo) => photo.url);
      if (uploaded.length) {
        savedPhotos[itemId] = uploaded.map(({ tempId, url }) => ({ tempId, url }));
      }
    }

    const hasContent =
      Object.values(checks).some(Boolean) ||
      Object.keys(answers).length > 0 ||
      Object.keys(savedPhotos).length > 0 ||
      memos.trim().length > 0;

    // 열어만 보고 나간 경우까지 남기면, 다음에 들어올 때마다 빈 초안을
    // 이어할지 묻게 된다.
    if (!hasContent) {
      InspectionDraft.clear(id, draftKey);
      return;
    }

    InspectionDraft.write(id, draftKey, {
      sessionId: sessionIdRef.current,
      fingerprint: fingerprintOf(templateItems),
      checks,
      subChecks,
      answers,
      photos: savedPhotos,
      memos,
    });
  }, [draftReady, section, draftKey, id, templateItems, checks, subChecks, answers, photos, memos]);

  // Auth Guard
  useEffect(() => {
    if (!user) {
      showAlert('알림', '로그인이 필요한 서비스입니다.', 'warning').then(() => {
        navigate(-1); // Go back
      });
    }
  }, [user, navigate, showAlert]);

  // Fetch Data
  useEffect(() => {
    if (!user) return; // Skip fetch if not logged in (will redirect)

    const fetchFacilityAndSection = async () => {
      try {
        // 정거장에서 시작한 점검은 계획이 정한 점검표를 쓴다. 구역이 들고 있는
        // 것을 읽으면 같은 장소를 다른 방식으로 점검할 수가 없다 -- 경비 순찰과
        // 정기순회가 같은 건물의 같은 구역을 돈다.
        if (stopIdParam) {
          const { data: stop } = await api.get(`/inspection-plans/stops/${stopIdParam}`);
          if (!stop) {
            setLoading(false);
            return;
          }
          setFacility(stop.facility);
          // 아래 화면은 전부 '구역 하나' 를 전제로 쓰여 있다. 정거장을 그 모양
          // 으로 맞춰 넘긴다. 건물 전체짜리 정거장은 구역이 없으므로 id 가 비고,
          // 그건 제출에서도 그대로 비운다.
          setSection({
            id: stop.section?.id ?? null,
            name: stop.section?.name ?? '건물 전체',
            checklistTemplate: stop.template,
            planName: stop.plan?.name,
          });
          setLoading(false);
          return;
        }

        // 정거장 없이 들어오는 길은 이제 없다. 예전에는 시설을 열어 구역을
        // 고르고 그 구역이 들고 있는 점검표로 점검했는데, 그 값은 지웠다 --
        // 무엇으로 점검할지는 계획이 정한다. 시설만 넘어오면 어느 계획의
        // 무엇을 하러 온 것인지 알 수 없으므로 짐작하지 않는다.
        setFacility(null);
      } catch (err) {
        console.error("Error fetching facility", err);
      } finally {
        setLoading(false);
      }
    };
    fetchFacilityAndSection();
  }, [id, stopIdParam, user]);

  const toggleCheck = (itemId) => {
    // Find item to check type
    const rawItem = section?.checklistTemplate?.items?.find(i => i.id === itemId);
    if (!rawItem) {
      setChecks(prev => ({
        ...prev,
        [itemId]: !prev[itemId]
      }));
      return;
    }

    // Normalize type for logic (simple check)
    const isCheckbox = rawItem.responseType === '체크박스' || rawItem.responseType === 'checkbox';

    setChecks(prev => {
      const newStatus = !prev[itemId];

      // If it's a checkbox, handle sub-items (Select All / Deselect All)
      if (isCheckbox && rawItem.options && rawItem.options.length > 0) {
        setSubChecks(prevSub => {
          const newSub = {};
          rawItem.options.forEach((_, idx) => {
            newSub[idx] = newStatus;
          });
          return {
            ...prevSub,
            [itemId]: newSub
          };
        });
      }

      return {
        ...prev,
        [itemId]: newStatus
      };
    });
  };

  const toggleSubCheck = (itemId, optionIndex, type) => {
    // Need raw item to get options count for "All Checked" logic
    const rawItem = section?.checklistTemplate?.items?.find(i => i.id === itemId);
    const options = rawItem?.options || [];

    setSubChecks(prev => {
      const currentItemChecks = prev[itemId] || {};
      let newItemChecks;

      if (type === 'choice') {
        // Radio behavior: only one true
        newItemChecks = { [optionIndex]: true };
      } else {
        // Checkbox behavior: toggle
        newItemChecks = {
          ...currentItemChecks,
          [optionIndex]: !currentItemChecks[optionIndex]
        };
      }

      // Determine Parent Status
      let parentComplete = false;
      if (type === 'choice') {
        // For choice, if we selected something, it's 'complete' enough (or assume true)
        parentComplete = true;
      } else {
        // Checkbox: ALL options must be checked to be "Complete"
        // Check if every index 0..length-1 is true
        if (options.length > 0) {
          parentComplete = options.every((_, idx) => newItemChecks[idx]);
        } else {
          // No options? fallback
          parentComplete = true;
        }
      }

      // Sync parent check status
      setChecks(prevChecks => ({
        ...prevChecks,
        [itemId]: parentComplete
      }));

      return {
        ...prev,
        [itemId]: newItemChecks
      };
    });
  };

  const setYesNo = (itemId, value) => {
    setAnswers(prev => ({ ...prev, [itemId]: value }));
    setChecks(prev => ({ ...prev, [itemId]: true }));
  };

  // 찍는 즉시 압축해서 올린다. 예전에는 제출할 때 전부 몰아서 올렸는데, 그러면
  // 원본 파일이 점검 내내 메모리에 쌓이고, 가장 실패하기 쉬운 순간이 마지막에
  // 몰린다 -- 거기서 실패하면 걸어 다니며 만든 기록이 통째로 날아갔다.
  const handlePhotoChange = async (itemId, e) => {
    const file = e.target.files?.[0];
    e.target.value = null;
    if (!file) return;

    if ((photos[itemId] || []).length >= MAX_PHOTOS_PER_ITEM) {
      showAlert('알림', `사진은 최대 ${MAX_PHOTOS_PER_ITEM}장까지 등록 가능합니다.`, 'warning');
      return;
    }

    // 올라가는 동안 보여줄 미리보기. 업로드가 끝나면 서버 URL 로 바꾸고
    // 이 참조는 풀어준다 -- 그래야 원본이 메모리에서 빠진다.
    const previewUrl = URL.createObjectURL(file);
    const tempId = `${Date.now()}-${Math.random()}`;
    setPhotos(prev => ({
      ...prev,
      [itemId]: [...(prev[itemId] || []), { tempId, previewUrl, url: null, uploading: true }]
    }));

    try {
      const compressed = await imageCompression(file, COMPRESSION_OPTIONS);
      const formData = new FormData();
      formData.append('file', compressed, compressed.name);
      formData.append('sessionId', sessionIdRef.current);
      formData.append('itemId', String(itemId));

      const res = await api.post('/reports/upload', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });

      URL.revokeObjectURL(previewUrl);
      setPhotos(prev => ({
        ...prev,
        [itemId]: (prev[itemId] || []).map(p =>
          p.tempId === tempId
            // url 은 제출할 값, viewUrl 은 화면에 그릴 값이다. 버킷이 비공개라
            // 저장용 주소로는 미리보기가 열리지 않는다.
            ? { tempId, url: res.data.url, viewUrl: res.data.viewUrl, uploading: false }
            : p
        )
      }));
    } catch (err) {
      URL.revokeObjectURL(previewUrl);
      setPhotos(prev => ({
        ...prev,
        [itemId]: (prev[itemId] || []).filter(p => p.tempId !== tempId)
      }));
      showAlert('오류', '사진 업로드에 실패했습니다. 다시 시도해주세요.', 'danger');
    }
  };

  // 즉시 올라가므로 잘못 찍은 사진은 서버에서도 지워야 한다.
  const handlePhotoRemove = async (itemId, tempId) => {
    const target = (photos[itemId] || []).find(p => p.tempId === tempId);
    if (!target || target.uploading) return;

    setPhotos(prev => ({
      ...prev,
      [itemId]: (prev[itemId] || []).filter(p => p.tempId !== tempId)
    }));
    try {
      await api.delete('/reports/upload', { data: { url: target.url } });
    } catch (err) {
      // 화면에서는 이미 뺐다. 서버에 남은 파일은 청소 잡이 하루 뒤 거둬간다.
      console.error('사진 삭제 실패', err);
    }
  };

  const handleSubmit = async () => {
    if (!section || isSubmitting) return;

    try {
      setIsSubmitting(true);

      // 1. Required items only. '답변 필수' was previously ignored and every
      //    item was forced, which made the setting meaningless.
      const answerable = templateItems.filter(isAnswerable);
      for (const item of answerable) {
        if (item.isRequired && !checks[item.id]) {
          showAlert('알림', `'${item.content}' 항목은 필수입니다.`, 'warning');
          setIsSubmitting(false);
          return;
        }
      }

      // Guard against submitting a completely empty inspection.
      if (answerable.length > 0 && !answerable.some(item => checks[item.id])) {
        showAlert('알림', '최소 1개 이상의 항목을 점검해주세요.', 'warning');
        setIsSubmitting(false);
        return;
      }

      // 2. Photo Validation based on Configuration
      const checkedItems = answerable.filter(item => checks[item.id]);

      for (const item of checkedItems) {
        // Handle both Korean and English mapping
        const isPhotoRequired = item.photoAttachmentOption === '필수' || item.photoAttachmentOption === 'required';
        if (isPhotoRequired) {
          if (!photos[item.id] || photos[item.id].length === 0) {
            showAlert('알림', `'${item.content}' 항목의 사진을 등록해주세요. (필수)`, 'warning');
            setIsSubmitting(false);
            return;
          }
        }
      }

      // 올라가는 중인 사진이 있으면 기다린다. 여기서 막지 않으면 URL 이 아직
      // 없는 사진이 조용히 빠진 채로 제출된다.
      if (Object.values(photos).flat().some(p => p.uploading)) {
        showAlert('알림', '사진이 아직 업로드 중입니다. 잠시 후 다시 눌러주세요.', 'warning');
        setIsSubmitting(false);
        return;
      }

      // 사진은 이미 촬영 시점에 올라가 있다. 여기서는 URL 만 모은다.
      const uploadedPhotoUrls = Object.values(photos)
        .flat()
        .map(p => p.url)
        .filter(Boolean);

      const checkedItemNames = checkedItems.map(item => {
        let content = `- ${item.content}`;

        // Append selected options if they exist
        if ((item.type === 'checkbox' || item.type === 'choice') && item.options) {
          const itemSubChecks = subChecks[item.id] || {};
          const selectedOptions = item.options.filter((_, idx) => itemSubChecks[idx]);
          if (selectedOptions.length > 0) {
            content += ` [${selectedOptions.join(', ')}]`;
          }
        }
        if (item.type === 'check_yn' && answers[item.id]) {
          content += ` [${answers[item.id]}]`;
        }
        return content;
      }).join('\n');
      const description = `점검 완료: ${checkedItems.length}/${answerable.length} 항목\n\n` +
        `[완료된 항목]\n${checkedItemNames}\n\n` +
        `[메모]\n${memos || '없음'}`;

      const { data: saved } = await api.post('/reports', {
        type: 'INSPECTION',
        description: description,
        // 정거장에서 시작했으면 그 정거장의 시설을 쓴다. 주소창의 값을 그대로
        // 믿으면 정거장과 다른 건물에 기록이 붙을 수 있다.
        facilityId: facility?.id ?? Number(id),
        sectionId: section.id ?? null,
        stopId: stopIdParam ? Number(stopIdParam) : undefined,
        photoUrls: uploadedPhotoUrls,
        sessionId: sessionIdRef.current,
      });

      InspectionDraft.clear(id, draftKey);

      // 회차가 아직 안 끝났으면 서버가 다음 곳을 알려준다. 목록으로 돌아가
      // 다시 찾게 하면 열세 곳을 도는 사람의 리듬이 끊긴다.
      const next = saved?.nextStop;
      if (next) {
        // '취소' 라고만 두면 방금 낸 제출을 무르는 것처럼 읽힌다. 제출은 이미
        // 끝났고 여기서 묻는 것은 '계속 돌 것인가' 뿐이므로, 첫 줄에서 끝났다고
        // 말하고 단추도 그렇게 이름 붙인다.
        const go = await showConfirm(
          '점검을 제출했습니다',
          `다음 곳은 ${next.name}입니다. 이 회차에 ${next.remaining}곳 남았습니다.`,
          'primary',
          { confirm: '다음 곳 점검', cancel: '여기까지' },
        );
        navigate(
          go
            ? `/facilities/${next.facilityId}/inspect?stopId=${next.stopId}`
            : '/today',
          { replace: true },
        );
        return;
      }

      showAlert('성공', '점검이 완료되었습니다.', 'success').then(() => {
        // 제출하고 나면 아직 남은 것이 보이는 곳으로 보낸다. 방금 한 일이
        // 목록에서 빠지는 것을 보는 것이 가장 확실한 확인이다.
        navigate('/today', { replace: true });
      });
    } catch (err) {
      console.error(err);
      showAlert('오류', '저장 실패: ' + (err.response?.data?.message || err.message), 'danger');
    } finally {
      setIsSubmitting(false);
    }
  };

  if (loading) return <div className="p-5 text-center">Loading...</div>;
  // 점검은 계획의 정거장에서 시작한다. 주소만 받아서는 어느 계획의 무엇을
  // 하러 온 것인지 알 수 없으므로, 짐작해서 아무 구역이나 열지 않고 되돌린다.
  if (!facility || !section) {
    return (
      <div className="p-5 text-center">
        <p className="text-muted">점검은 「오늘 할 점검」에서 시작해 주세요.</p>
        <button className="btn btn-primary rounded-pill px-4" onClick={() => navigate('/today')}>
          오늘 할 점검
        </button>
      </div>
    );
  }



  // Check if everything is ready to avoid flicker
  if (!user) return null;

  return (
    // px-3 matches the header's -1rem pull: without it the header hangs 1rem
    // past each edge and the page scrolls sideways, clipping "점검" on the left
    // and "이슈 등록" on the right, while the cards sit flush to the glass.
    <div className="bg-light min-vh-100 pb-5 px-3">
      <style>{`
                .inspection-header {
                    background: white;
                    color: #1e1b4b;
                    /* Layout 밖의 독립 라우트 — 상태바 높이를 직접 더한다. */
                    padding: calc(0.75rem + var(--safe-area-inset-top)) 1rem 0.75rem;
                    display: flex;
                    align-items: center;
                    border-bottom: 1px solid #f0f0f0;
                    margin: 0 -1rem;
                    position: sticky;
                    top: 0;
                    z-index: 1000;
                }
            .section-title-card {
                background: white;
                padding: 1.5rem;
                margin-bottom: 1rem;
                border-bottom: 1px solid #f0f0f0;
            }
            .check-btn {
                background: #f1f3f5;
                border: 1px solid #ced4da;
                color: #495057;
                padding: 0.5rem 1rem;
                border-radius: 8px;
                font-weight: 600;
                min-width: 80px;
                transition: all 0.2s;
            }
            .check-btn.active {
                background: var(--primary-color);
                border-color: var(--primary-color);
                color: white;
                box-shadow: 0 2px 4px rgba(79, 70, 229, 0.4);
            }
            .form-section {
                background: white;
                border-radius: 20px;
                padding: 1.25rem 1rem;
                margin-bottom: 1.5rem;
                box-shadow: 0 2px 10px rgba(0,0,0,0.02);
            }
            .photo-box {
                background: #f8f9fa;
                border-radius: 12px;
                padding: 1rem;
                margin-top: 1rem;
            }
            .photo-thumb {
                position: relative;
                display: inline-block;
                flex: 0 0 auto;
            }
            .photo-spinner {
                position: absolute;
                top: 50%;
                left: 50%;
                transform: translate(-50%, -50%);
            }
            .photo-remove {
                position: absolute;
                top: 2px;
                right: 10px;
                width: 20px;
                height: 20px;
                padding: 0;
                border: none;
                border-radius: 50%;
                background: rgba(0, 0, 0, 0.55);
                color: white;
                line-height: 1;
                display: flex;
                align-items: center;
                justify-content: center;
            }
            .photo-preview {
                width: 60px;
                height: 60px;
                border-radius: 8px;
                object-fit: cover;
                margin-right: 8px;
            }
            .submit-btn {
                position: fixed;
                /* 화면 바닥에 붙인다. 한때 버튼 전체를 인셋만큼 띄웠는데, 그러면
                   뷰포트가 이미 내비게이션바 위에서 끝나는 기기에서 버튼이 붕 뜨고
                   그 사이로 페이지가 비쳤다. 플러그인은 시스템 바 높이를 그대로
                   알려줄 뿐, WebView 가 정말 그 바 밑까지 그려지는지는 안드로이드
                   버전과 테마에 따라 다르다. JS 로는 상단·하단 중 어느 쪽이 겹치는지
                   가려낼 수 없다.
                   그래서 구분하지 않는다. 붙여 두고 아래 여백으로만 비운다.
                   겹치는 기기에서는 글자가 바 위로 올라오고, 겹치지 않는 기기에서는
                   그냥 조금 두툼한 버튼이 된다. 어느 쪽도 가려지거나 뜨지 않는다. */
                bottom: 0;
                left: 0;
                right: 0;
                background: #4f46e5;
                color: white;
                font-weight: 700;
                border: none;
                /* 인셋이 있으면 그보다 0.5rem 더 -- 시스템 바에 딱 붙어 보이지
                   않을 만큼. 없으면 평소 여백. */
                padding: 1.25rem 1.25rem
                    max(1.25rem, calc(var(--safe-area-inset-bottom) + 0.5rem));
                font-size: 1.1rem;
                z-index: 1000;
                box-shadow: 0 -4px 15px rgba(0,0,0,0.08);
            }
            .form-control:focus {
                border-color: var(--primary-color);
                box-shadow: 0 0 0 0.25rem rgba(79, 70, 229, 0.25);
            }
        `}</style>

      {/* Header */}
      <div className="inspection-header">
        {/* 들어온 자리로 돌려보낸다. 시설 상세에서 왔는지 매트릭스에서 왔는지에
            따라 돌아갈 곳이 다른데, 한 곳으로 고정하면 한쪽은 늘 헤매게 된다. */}
        <button className="btn border-0 p-0 me-3 text-dark" onClick={() => navigate(-1)}>
          <i className="bi bi-chevron-left fs-4"></i>
        </button>
        <h5 className="mb-0 fw-bold">점검</h5>
        <div className="ms-auto fw-bold text-muted" style={{ fontSize: '0.9rem' }}>이슈 등록</div>
      </div>

      {/* Section Info */}
      <div className="section-title-card">
        <div className="d-flex justify-content-between align-items-center mb-2">
          <h4 className="fw-bold m-0">{facility.name}</h4>
        </div>
        <div className="text-muted small">
          {section.planName ? `${section.planName} · ` : ''}{section.name}
        </div>
      </div>

      {/* Items List */}
      <div className="pb-5 mb-5">
        <div className="mb-3 text-muted fw-bold d-flex justify-content-between align-items-center">
          <span>
            점검 항목
            <span className="ms-2 fw-normal" style={{ fontSize: '0.85rem' }}>
              {templateItems.filter(i => isAnswerable(i) && checks[i.id]).length}
              {' / '}
              {templateItems.filter(isAnswerable).length}
            </span>
          </span>
          <span className="small">{new Date().toLocaleDateString()}</span>
        </div>

        {templateItems.map(item => {
          const isPhotoRequired = item.photoAttachmentOption === '필수' || item.photoAttachmentOption === 'required';
          const isPhotoAllowed = item.photoAttachmentOption !== 'none' && item.photoAttachmentOption !== '기능 미사용';

          // A section is a divider that groups the items under it.
          if (item.type === 'SECTION') {
            return (
              <div key={item.id} className="mt-4 mb-2 px-1">
                <div className="fw-bold text-uppercase" style={{ fontSize: '0.8rem', letterSpacing: '0.05em', color: 'var(--primary-color)' }}>
                  {item.content}
                </div>
                {item.description && (
                  <div className="text-muted mt-1" style={{ fontSize: '0.8rem' }}>{item.description}</div>
                )}
                <hr className="mt-2 mb-0" style={{ opacity: 0.15 }} />
              </div>
            );
          }

          return (
            <div key={item.id} className="bg-white p-3 rounded-3 shadow-sm mb-3">
              <div className="d-flex justify-content-between align-items-start mb-3">
                <div style={{ maxWidth: '75%' }}>
                  <span className="fw-bold fs-6" style={{ lineHeight: '1.4' }}>{item.content}</span>
                  {item.isRequired && <span className="text-danger ms-1 fw-bold">*</span>}

                  {/* Render Options (Sub-items) */}
                  {(item.type === 'checkbox' || item.type === 'choice') && item.options && item.options.length > 0 && (
                    <div className="mt-3 ps-2 border-start border-3">
                      {item.options.map((option, idx) => (
                        <div key={idx} className="form-check mb-2" onClick={() => toggleSubCheck(item.id, idx, item.type)}>
                          <input
                            className="form-check-input"
                            type={item.type === 'choice' ? 'radio' : 'checkbox'}
                            name={`option-${item.id}`}
                            checked={(subChecks[item.id] || {})[idx] || false}
                            onChange={() => { }} // Handled by div click
                            style={{ cursor: 'pointer' }}
                          />
                          <label className="form-check-label" style={{ cursor: 'pointer' }}>
                            {option}
                          </label>
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                {item.type === 'check_yn' ? (
                  <div className="d-flex gap-2 flex-shrink-0">
                    {['네', '아니오'].map(option => (
                      <button
                        key={option}
                        className={`check-btn ${answers[item.id] === option ? 'active' : ''}`}
                        onClick={() => setYesNo(item.id, option)}
                      >
                        {option}
                      </button>
                    ))}
                  </div>
                ) : (
                  <button
                    className={`check-btn ${checks[item.id] ? 'active' : ''}`}
                    onClick={() => toggleCheck(item.id)}
                  >
                    {checks[item.id] ? '완료' : '체크'}
                  </button>
                )}
              </div>

              {isPhotoAllowed && (
                <div className="photo-box">
                  <div className="d-flex justify-content-between align-items-center mb-2">
                    <div>
                      <span className="fw-bold me-1 text-dark" style={{ fontSize: '0.9rem' }}>
                        사진 {isPhotoRequired && <span className="text-danger">*</span>}
                      </span>
                      <span className="text-muted small">{(photos[item.id] || []).length}/{MAX_PHOTOS_PER_ITEM}</span>
                    </div>
                    <label htmlFor={`photo-upload-${item.id}`}>
                      <i className="bi bi-plus fs-3 text-muted" style={{ cursor: 'pointer' }}></i>
                    </label>
                    <input
                      id={`photo-upload-${item.id}`}
                      type="file"
                      accept="image/*"
                      capture="environment"
                      className="d-none"
                      onChange={(e) => handlePhotoChange(item.id, e)}
                    />
                  </div>

                  {/* Photo Previews */}
                  <div className="d-flex overflow-auto">
                    {(photos[item.id] || []).map((photo) => (
                      <div key={photo.tempId} className="photo-thumb">
                        <img
                          src={photo.previewUrl || photo.viewUrl || photo.url}
                          alt="preview"
                          className="photo-preview border"
                          style={photo.uploading ? { opacity: 0.4 } : undefined}
                        />
                        {photo.uploading ? (
                          <span className="photo-spinner spinner-border spinner-border-sm text-primary" role="status" />
                        ) : (
                          <button
                            type="button"
                            className="photo-remove"
                            onClick={() => handlePhotoRemove(item.id, photo.tempId)}
                            aria-label="사진 삭제"
                          >
                            <i className="bi bi-x"></i>
                          </button>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )
        })}

        {/* Memo Section */}
        <div className="mt-4 mb-5">
          <h5 className="fw-bold mb-3 fs-6">메모</h5>
          <textarea
            className="form-control rounded-3 p-3 border-0 shadow-sm"
            rows="4"
            placeholder="메모를 입력해주세요"
            style={{ resize: 'none' }}
            value={memos}
            onChange={(e) => setMemos(e.target.value)}
          ></textarea>
        </div>
      </div>

      {/* Submit Button */}
      <button className="submit-btn" onClick={handleSubmit} disabled={isSubmitting}>
        {isSubmitting ? '제출 중...' : '제출'}
      </button>
    </div>
  );
};

export default InspectionPage;